import React, {useEffect, useRef, useState} from 'react';
import {Box, Grid, InputLabel, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from '@mui/material';
import {useTheme} from "@mui/material/styles";
import {isEqual} from "lodash";
import {variableDescriptions} from "../../utils/choices";

const DataFileSelector = ({ data, paramSetter }) => {
  const theme = useTheme();
  const [selectedVars, setSelectedVars] = useState([]);
  const [toggled, setToggled] = useState(false);
  const prevDataRef = useRef(null);

  useEffect(() => {
    if (toggled) {
      const allVars = Object.entries(data)
        .filter(([key, value]) => key.startsWith('has_') && value)
        .map(([key]) => key.replace('has_', ''));
      allVars.push('ALL');
      setSelectedVars(allVars);
    } else {
      setSelectedVars([]);
    }
  }, [toggled]);

  useEffect(() => {
    if (!isEqual(prevDataRef.current, data)) {
      setSelectedVars([]);
      prevDataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    paramSetter((prevData) => ({ ...prevData, files: selectedVars }));
  }, [selectedVars]);

  // Extract keys like 'br', 'bt', etc. where has_<key> === true
  const availableVars = Object.entries(data)
    .filter(([key, value]) => key.startsWith('has_') && value)
    .map(([key]) => key.replace('has_', ''));

  const handleSelection = (event, newSelection) => {
    setSelectedVars(newSelection);
  };

  const toggleAll = () => {
    setToggled(!toggled);
  };

  return (
    <Box>
      <InputLabel variant={'primary'} sx={{ mb: 1 }}>
        Select Data Files
      </InputLabel>
      <ToggleButtonGroup
        value={selectedVars}
        onChange={handleSelection}
        aria-label="variable selector"
        color="primary"
        fullWidth={true}
        sx={{
          display: 'flex', flexWrap: 'wrap', gap: 2,
          '& .MuiToggleButtonGroup-grouped': {
            borderRadius: 2, // each button gets its own radius
          },
        }}
      >
        <ToggleButton
          key={'ALL'}
          value={'ALL'}
          aria-label={'ALL'}
          onClick={toggleAll}
          sx={{
            width: '50px',
            height: '45px',
            borderRadius: '12px',
            fontWeight: 'normal',
            fontSize: '11px',
            lineHeight: 1,
            background: theme.palette.base.rothko5,
            color: 'black',
            boxShadow: '5px 5px 10px #bebebe, -5px -5px 10px #ffffff',
            transition: 'all 0.2s ease-in-out',
            border: 'none',
            '&.Mui-selected': {
              background: theme.palette.base.rothko5,
              boxShadow: 'inset 5px 5px 10px #bebebe, inset -5px -5px 10px #ffffff',
              color: theme.palette.blues.rothko2,
              fontWeight: 'bold',
              fontSize: '12px',
              textShadow: '2px 2px 2px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                background: theme.palette.base.rothko5,
              },
            },
            '&:hover': {
              background: theme.palette.base.rothko5,
              boxShadow: 'inset 5px 5px 10px #bebebe, inset -5px -5px 10px #ffffff',
            },
          }}
        >
          {selectedVars.includes('ALL') ? 'CLEAR' : 'ALL'}
        </ToggleButton>
        {availableVars.map((variable) => (
          <Tooltip
            key={`var-${variable}`}
            title={variableDescriptions[variable]}
            slotProps={{               // MUI v5.13+ / v6
              tooltip: { sx: { fontSize: 9 } },
            }}
            arrow
          >
            <ToggleButton
              key={variable}
              value={variable}
              aria-label={variable}
              sx={{
                width: '50px',
                height: '45px',
                borderRadius: '12px',
                fontWeight: 'normal',
                fontSize: '11px',
                lineHeight: 1,
                background: theme.palette.base.rothko5,
                color: 'black',
                boxShadow: '5px 5px 10px #bebebe, -5px -5px 10px #ffffff',
                transition: 'all 0.2s ease-in-out',
                border: 'none',
                '&.Mui-selected': {
                  background: theme.palette.base.rothko5,
                  boxShadow: 'inset 5px 5px 10px #bebebe, inset -5px -5px 10px #ffffff',
                  color: theme.palette.blues.rothko2,
                  fontWeight: 'bold',
                  fontSize: '12px',
                  textShadow: '2px 2px 2px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    background: theme.palette.base.rothko5,
                  },
                },
                '&:hover': {
                  background: theme.palette.base.rothko5,
                  boxShadow: 'inset 5px 5px 10px #bebebe, inset -5px -5px 10px #ffffff',
                },
              }}
            >
              {variable.toUpperCase()}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default DataFileSelector;
